import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuthStore } from "../stores/auth";

/**
 * Wraps children with Auth0 authentication gate.
 * Shows login redirect if not authenticated, loads user context after authentication.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const {
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    getAccessTokenSilently,
    error: auth0Error,
  } = useAuth0();

  const { user, isLoading: profileLoading, fetchUser } = useAuthStore();
  const [accessDenied, setAccessDenied] = useState(false);

  // Check for Auth0 error in URL (from Post-Login Action deny)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    if (error) {
      console.error("Auth0 error:", error, params.get("error_description"));
      setAccessDenied(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (auth0Error) {
      console.error("Auth0 SDK error:", auth0Error.message);
      setAccessDenied(true);
    }
  }, [auth0Error]);

  useEffect(() => {
    if (!auth0Loading && !isAuthenticated && !accessDenied) {
      loginWithRedirect();
    }
  }, [auth0Loading, isAuthenticated, accessDenied, loginWithRedirect]);

  useEffect(() => {
    if (isAuthenticated && !user && !profileLoading) {
      fetchUser(getAccessTokenSilently);
    }
  }, [isAuthenticated, user, profileLoading, fetchUser, getAccessTokenSilently]);

  if (accessDenied) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "100vh", fontFamily: "system-ui",
        padding: "2rem",
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: 12,
          background: "#fee2e2", display: "flex", alignItems: "center",
          justifyContent: "center", marginBottom: 24, fontSize: 28,
        }}>
          🚫
        </div>
        <h1 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: 8 }}>
          Access denied
        </h1>
        <p style={{ color: "#666", textAlign: "center", maxWidth: 360, marginBottom: 24 }}>
          Your account has not been provisioned for access to PiStack Manager.
          Please contact your administrator.
        </p>
        <a href="mailto:info@syncstack.ai" style={{ color: "#2563eb", fontSize: "0.875rem" }}>
          info@syncstack.ai
        </a>
      </div>
    );
  }

  if (auth0Loading || !isAuthenticated) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "100vh", fontFamily: "system-ui",
      }}>
        <div style={{
          width: 32, height: 32, border: "3px solid #e5e7eb",
          borderTopColor: "#2563eb", borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <p style={{ color: "#666", marginTop: 16, fontSize: "0.875rem" }}>Connecting...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (profileLoading || !user) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", minHeight: "100vh", fontFamily: "system-ui",
      }}>
        <div style={{
          width: 32, height: 32, border: "3px solid #e5e7eb",
          borderTopColor: "#2563eb", borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <p style={{ color: "#666", marginTop: 16, fontSize: "0.875rem" }}>Loading profile...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return <>{children}</>;
}
